import React, { useState } from 'react';
import { Database, Server, Smartphone, Brain, Shield, Zap, ChevronDown, ChevronUp } from 'lucide-react';

const techStack = [
  {
    id: 'deepgram',
    name: 'Deepgram Aura',
    category: 'Transcription',
    description: 'Industry-leading real-time speech-to-text with sub-300ms latency.',
    icon: <Zap className="w-6 h-6" />,
    color: 'from-cyan-500 to-blue-500',
    features: ['Real-time streaming', '50+ languages', 'Speaker diarization', '98% accuracy']
  },
  {
    id: 'gemini',
    name: 'Gemini 2.5 Flash',
    category: 'AI Intelligence',
    description: "Google's fastest multimodal AI for instant explanations and summaries.",
    icon: <Brain className="w-6 h-6" />,
    color: 'from-purple-500 to-indigo-500',
    features: ['Sub-second responses', 'Context-aware', 'Multi-turn conversations', 'Cost-effective']
  },
  {
    id: 'react',
    name: 'React',
    category: 'Frontend',
    description: 'Modern web application with responsive design and real-time updates.',
    icon: <Smartphone className="w-6 h-6" />,
    color: 'from-slate-600 to-slate-800',
    features: ['Single Page App', 'Hot reload', 'Component-based', 'Rich ecosystem']
  },
  {
    id: 'mongodb',
    name: 'MongoDB',
    category: 'Backend',
    description: 'Flexible document database for storing lectures, notes, and user data.',
    icon: <Database className="w-6 h-6" />,
    color: 'from-emerald-500 to-green-500',
    features: ['Document storage', 'Real-time queries', 'Flexible schema', 'Scalable']
  },
  {
    id: 'fastapi',
    name: 'FastAPI',
    category: 'API Layer',
    description: 'High-performance Python API framework for AI service orchestration.',
    icon: <Server className="w-6 h-6" />,
    color: 'from-green-600 to-lime-500',
    features: ['Async support', 'Auto documentation', 'Type validation', 'Fast performance']
  },
  {
    id: 'security',
    name: 'Enterprise Security',
    category: 'Privacy',
    description: 'FERPA & GDPR compliant with end-to-end encryption and local processing.',
    icon: <Shield className="w-6 h-6" />,
    color: 'from-amber-500 to-orange-500',
    features: ['E2E encryption', 'Local-only mode', 'No data selling', 'SOC 2 compliant']
  }
];

const TechStack = () => {
  const [expandedItem, setExpandedItem] = useState(null);

  return (
    <section id="tech-stack" className="py-24 bg-slate-900" data-testid="tech-stack-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-slate-800 text-slate-300 rounded-full px-4 py-2 mb-4 border border-slate-700">
            <Server className="w-4 h-4" />
            <span className="text-sm font-medium">Technical Architecture</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            Built on 
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-400">
              Modern Infrastructure
            </span>
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            A carefully selected tech stack optimized for speed, reliability, and developer experience.
          </p>
        </div>

        {/* Architecture diagram */}
        <div className="mb-16 p-8 bg-slate-800/50 rounded-2xl border border-slate-700">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            {/* Frontend App */}
            <div className="text-center">
              <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-slate-600 to-slate-800 flex items-center justify-center mb-3 border border-slate-600">
                <Smartphone className="w-10 h-10 text-white" />
              </div>
              <div className="text-white font-semibold">Web App</div>
              <div className="text-slate-500 text-sm">React / TailwindCSS</div>
            </div>

            {/* Arrow */}
            <div className="hidden lg:flex items-center">
              <div className="w-16 h-0.5 bg-gradient-to-r from-slate-600 to-cyan-500"></div>
              <div className="w-0 h-0 border-t-4 border-b-4 border-l-8 border-transparent border-l-cyan-500"></div>
            </div>

            {/* API Layer */}
            <div className="text-center">
              <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-green-600 to-lime-500 flex items-center justify-center mb-3">
                <Server className="w-10 h-10 text-white" />
              </div>
              <div className="text-white font-semibold">API Layer</div>
              <div className="text-slate-500 text-sm">FastAPI / Python</div>
            </div>

            {/* Arrow */}
            <div className="hidden lg:flex items-center">
              <div className="w-16 h-0.5 bg-gradient-to-r from-lime-500 to-purple-500"></div>
              <div className="w-0 h-0 border-t-4 border-b-4 border-l-8 border-transparent border-l-purple-500"></div>
            </div>

            {/* AI Services */}
            <div className="text-center">
              <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center mb-3">
                <Brain className="w-10 h-10 text-white" />
              </div>
              <div className="text-white font-semibold">AI Services</div>
              <div className="text-slate-500 text-sm">Gemini / Deepgram</div>
            </div>

            {/* Arrow */}
            <div className="hidden lg:flex items-center">
              <div className="w-16 h-0.5 bg-gradient-to-r from-indigo-500 to-emerald-500"></div>
              <div className="w-0 h-0 border-t-4 border-b-4 border-l-8 border-transparent border-l-emerald-500"></div>
            </div>

            {/* Database */}
            <div className="text-center">
              <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-emerald-500 to-green-500 flex items-center justify-center mb-3">
                <Database className="w-10 h-10 text-white" />
              </div>
              <div className="text-white font-semibold">Database</div>
              <div className="text-slate-500 text-sm">MongoDB</div>
            </div>
          </div>
        </div>

        {/* Tech cards grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {techStack.map((tech) => (
            <div
              key={tech.id}
              className="group bg-slate-800/50 rounded-2xl border border-slate-700 overflow-hidden hover:border-slate-600 transition-all duration-300"
              data-testid={`tech-card-${tech.id}`}
            >
              <div className={`h-1 bg-gradient-to-r ${tech.color}`}></div>
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${tech.color} flex items-center justify-center text-white`}>
                    {tech.icon}
                  </div>
                  <span className="text-xs font-medium text-slate-500 bg-slate-700/50 px-2 py-1 rounded-full">
                    {tech.category}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{tech.name}</h3>
                <p className="text-slate-400 text-sm mb-4">{tech.description}</p>
                
                <button
                  onClick={() => setExpandedItem(expandedItem === tech.id ? null : tech.id)}
                  className="flex items-center gap-1 text-sm font-medium text-purple-400 hover:text-purple-300 transition-colors"
                >
                  {expandedItem === tech.id ? 'Hide features' : 'View features'}
                  {expandedItem === tech.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>

                <div className={`overflow-hidden transition-all duration-300 ${
                  expandedItem === tech.id ? 'max-h-40 mt-4' : 'max-h-0'
                }`}>
                  <ul className="space-y-2">
                    {tech.features.map((feature, index) => (
                      <li key={feature} className="flex items-center gap-2 text-sm text-slate-400">
                        <div className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${tech.color}`}></div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TechStack;
